import { Grid, space } from "@waldner-laboreinrichtungen/component-system";
import type { FC } from "react";
import { useState } from "react";
import styledComponents from "styled-components";

import { GridStatusRow } from "../custom/common/function-grid-helpers.tsx";
import { FunctionGrid as FumehoodFunctionGrid } from "../custom/fumehood";
import { FunctionGrid as SensorPanelFunctionGrid } from "../custom/sensorpanel";
import { FunctionGrid as SmartModuleFunctionGrid } from "../custom/smart-module";
import { FunctionGrid as SphereFunctionGrid } from "../custom/sphere";
import { FunctionGrid as SpiderFunctionGrid } from "../custom/spider";
import { useUnitStore } from "../store/unit-store";
import type { Channel, Incident, Unit } from "../view-model";
import { ChannelThresholdConfig } from "./channel-threshold-config";
import { ErrorScreenWithText } from "./error-screen";
import type { FunctionGridProps } from "./function-grid.tsx";
import { FunctionGrid } from "./function-grid.tsx";

export type GenericChannelValuesProps = {
  unit: Unit;
  channels: Channel[];
  incidents?: Incident[];
  onRefetch?: () => void;
};

const StyledTabWrapper = styledComponents("div")`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const StyledStatusGrid = styledComponents(Grid)`
  grid-row-gap: ${space(3)};
  grid-column-gap: ${space(1)};
  margin-top: ${space(3)};
  margin-bottom: ${space(3)};
`;

export const UnitFunctions: FC<GenericChannelValuesProps> = ({
  channels,
  incidents,
  onRefetch,
  unit,
}) => {
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(
    null
  );
  const selectedChannel =
    channels.find((c) => c.id === selectedChannelId) ?? null;

  // Subscribe to live State and ActiveProgram updates from unit store
  const storeUnit = useUnitStore((state) => state.units[unit.id]);
  const activeProgram = storeUnit?.activeProgram ?? unit.activeProgram;
  const unitState = storeUnit?.state ?? unit.state;

  if (!channels) {
    return <ErrorScreenWithText error={new Error("no channels found")} />;
  }
  const GridComponent = getGridComponent(unit.type);
  return (
    <StyledTabWrapper>
      <StyledStatusGrid>
        <GridStatusRow activeProgram={activeProgram} unitState={unitState} />
      </StyledStatusGrid>
      <GridComponent
        activeProgram={activeProgram}
        functions={channels}
        incidents={incidents}
        onSelect={(ch) => setSelectedChannelId(ch?.id ?? null)}
        unitState={unitState}
      />
      {selectedChannel && (
        <ChannelThresholdConfig
          channel={selectedChannel}
          deviceId={unit.id}
          isOpen={!!selectedChannel}
          onClose={() => setSelectedChannelId(null)}
          onSave={onRefetch}
        />
      )}
    </StyledTabWrapper>
  );
};

function getGridComponent(unitType: string): FC<FunctionGridProps> {
  switch (unitType) {
    case "FumehoodFunctionalUnit":
    case "FumehoodUnitType": {
      return FumehoodFunctionGrid;
    }
    case "EssentimSphereUnitType":
    case "SphereUnitType": {
      return SphereFunctionGrid;
    }
    case "WaldnerSensorpanelUnitType":
    case "SensorpanelUnitType": {
      return SensorPanelFunctionGrid;
    }
    case "EssentimSpiderUnitType":
    case "SpiderUnitType": {
      return SpiderFunctionGrid;
    }
    case "SmartModuleUnitType": {
      return SmartModuleFunctionGrid;
    }
    default: {
      return FunctionGrid;
    }
  }
}
